// @givmo/fundraisers-module — the organization's sending domain (EMC-WEB3; Email Campaign plan §2.8,
// D-EMC-5): its queries and the two acts, over the wire in `sender-wire/` (which holds the backend's
// own words and translates them). While email campaigns are dark here every operation is the route
// miss, which the read answers as `null`: nothing to show, never "not set up".

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import {
  checkSenderRecords,
  createSenderIdentity,
  decodeSenderIdentity,
  decodeSenderRecords,
  fetchSenderIdentity,
  fetchSenderRecords,
  isSenderUnavailable,
  type SenderIdentity,
  type SenderIdentityInput,
  type SenderRecord,
  type SenderRecords,
} from './sender-wire/sender-wire';

export {
  decodeSenderIdentity,
  decodeSenderRecords,
  type SenderDmarcStatus,
  type SenderIdentity,
  type SenderIdentityInput,
  type SenderRecord,
  type SenderRecordStatus,
  type SenderRecords,
  type SenderState,
} from './sender-wire/sender-wire';

/** The three CNAMEs, in the server's order; the DMARC row is apart (shown only when `needed`). */
export function cnameRecords(read: SenderRecords): SenderRecord[] {
  return read.records.filter((item) => item.type === 'cname');
}

/**
 * A record's name as a DNS panel that appends the zone wants it: `em4829` for `em4829.kenchild.org`,
 * and `em6782.mail` for a subdomain sender's `em6782.mail.givmocharitable.org`, since the zone is
 * the registrable domain (`SenderRecords.zone`), never the sending domain. The server sends full
 * names (review-8's note 5); many DNS screens add the zone themselves.
 */
/**
 * The domain whose DMARC record covers the sending domain: the one the server found it at
 * (`givmocharitable.org` for `_dmarc.givmocharitable.org`), else the sending domain itself.
 */
export function dmarcDomain(read: SenderRecords): string {
  const domain = read.domain ?? '';
  const found = read.dmarc?.foundAt;
  return found?.toLowerCase().startsWith('_dmarc.') ? found.slice('_dmarc.'.length) : domain;
}

export function relativeHost(host: string, zone: string | null): string {
  if (!zone) return host;
  const suffix = `.${zone.toLowerCase()}`;
  const lower = host.toLowerCase();
  return lower.endsWith(suffix) ? host.slice(0, host.length - suffix.length) : host;
}

export const senderKeys = {
  all: (orgRef: string | null) => ['fundraisers-module', 'sender-identity', orgRef] as const,
  identity: (orgRef: string | null) => [...senderKeys.all(orgRef), 'identity'] as const,
  records: (orgRef: string | null) => [...senderKeys.all(orgRef), 'records'] as const,
};

/** No retry for an answer (dark here, or not this person's); one for a blip. */
function retry(failureCount: number, error: unknown): boolean {
  return (
    !(isGivmoError(error) && (error.status === 404 || error.status === 403)) && failureCount < 1
  );
}

/**
 * The organization's sending domain. `data` is `null` while email campaigns are dark here (the route
 * miss) or the answer is unreadable; the Emails tab reads `data?.canSchedule === true` before it
 * offers a schedule.
 */
export function useSenderIdentity(orgRef: string | null) {
  return useQuery({
    queryKey: senderKeys.identity(orgRef),
    enabled: orgRef !== null,
    queryFn: async ({ signal }): Promise<SenderIdentity | null> => {
      try {
        return decodeSenderIdentity(await fetchSenderIdentity(orgRef as string, { signal }));
      } catch (error) {
        if (isSenderUnavailable(error)) return null;
        throw error;
      }
    },
    retry,
  });
}

export function useSenderRecords(orgRef: string | null, enabled: boolean) {
  return useQuery({
    queryKey: senderKeys.records(orgRef),
    enabled: orgRef !== null && enabled,
    queryFn: async ({ signal }) =>
      decodeSenderRecords(await fetchSenderRecords(orgRef as string, { signal })),
    retry,
  });
}

/** Set up (or change) the sending address; both reads refresh from the server's answer. */
export function useCreateSenderIdentity(orgRef: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SenderIdentityInput) => createSenderIdentity(orgRef as string, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: senderKeys.all(orgRef) }),
  });
}

/** Check records now. Its answer is the records read, put in place; the identity read refreshes. */
export function useCheckSenderRecords(orgRef: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => decodeSenderRecords(await checkSenderRecords(orgRef as string)),
    onSuccess: (read) => {
      if (read) queryClient.setQueryData(senderKeys.records(orgRef), read);
      return queryClient.invalidateQueries({ queryKey: senderKeys.identity(orgRef) });
    },
  });
}
